import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Edit,
  Trash2,
  Plus,
  FileText,
  Power,
  PowerOff,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Workflow, WorkflowStage, StageData, WorkflowContext } from './types';
import { ApprovalStageWizard } from './ApprovalStageWizard';
import { WorkflowViewToggle } from './WorkflowViewToggle';
import type { WorkflowView } from './WorkflowViewToggle';
import { WorkflowListView } from './WorkflowListView';
import { WorkflowVisualFlow } from './WorkflowVisualFlow';
import type { ApprovalStage } from '@/types/approvalWorkflow';
import { findPlaceholderStages } from './placeholderStageUtils';
import { PlaceholderStageWarning } from './PlaceholderStageWarning';

interface WorkflowDetailsPageProps {
  workflow: Workflow;
  onBack: () => void;
  onUpdateStage: (stageId: string, stageData: StageData) => void;
  onSaveDraft?: (stageId: string, stageData: StageData) => Promise<void>; // Save draft to backend without closing wizard
  onDeleteStage?: (stageId: string) => void;
  onAddStage?: (stageName?: string) => void; // UPDATED: Accept optional stage name
  initialEditingStageId?: string | null;
  onClearInitialEditingStageId?: () => void;
  initialStageName?: string; // NEW: Pre-fill stage name when creating from placeholder
  onUpdateWorkflowStatus?: (workflowId: string, newStatus: Workflow['status']) => void; // NEW: Status update callback
}

export function WorkflowDetailsPage({
  workflow,
  onBack,
  onUpdateStage,
  onSaveDraft,
  onDeleteStage,
  onAddStage,
  initialEditingStageId,
  onClearInitialEditingStageId,
  initialStageName,
  onUpdateWorkflowStatus,
}: WorkflowDetailsPageProps) {
  const [editingStageId, setEditingStageId] = useState<string | null>(initialEditingStageId || null);
  const [wizardOpen, setWizardOpen] = useState(!!initialEditingStageId);
  const [currentView, setCurrentView] = useState<WorkflowView>('list');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<Workflow['status'] | null>(null);

  // Detect placeholder stages
  const placeholders = findPlaceholderStages(workflow);
  const hasPlaceholders = placeholders.length > 0;

  // Watch for initialEditingStageId changes (when "Add Stage" or "Save & Add Another" is clicked)
  React.useEffect(() => {
    if (initialEditingStageId) {
      setEditingStageId(initialEditingStageId);
      setWizardOpen(true);
    }
  }, [initialEditingStageId]);

  const editingStage = workflow.stages.find((s) => s.id === editingStageId);

  const handleEditStage = (stageId: string) => {
    setEditingStageId(stageId);
    setWizardOpen(true);
  };

  const handleSaveStage = (stageData: StageData) => {
    if (editingStageId) {
      onUpdateStage(editingStageId, stageData);
    }
    setWizardOpen(false);
    setEditingStageId(null);
    if (onClearInitialEditingStageId) {
      onClearInitialEditingStageId();
    }
  };

  const handleSaveAndAddAnother = () => {
    // Save the current stage first (data is already saved via handleSaveStage)
    // Then trigger parent to create a new stage
    if (onAddStage) {
      onAddStage();
    }
    
    // The wizard will stay open and switch to the new stage via initialEditingStageId
    // No need to close or reset here - the parent will handle it
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
    setEditingStageId(null);
    if (onClearInitialEditingStageId) {
      onClearInitialEditingStageId();
    }
  };

  const handleStatusChangeRequest = (newStatus: Workflow['status']) => {
    setPendingStatus(newStatus);
    setStatusDialogOpen(true);
  };

  const handleStatusChangeConfirm = () => {
    if (pendingStatus && onUpdateWorkflowStatus) {
      onUpdateWorkflowStatus(workflow.id, pendingStatus);
      toast.success(`Workflow status updated to ${pendingStatus}`);
    }
    setStatusDialogOpen(false);
    setPendingStatus(null);
  };

  const getStatusDialogContent = () => {
    if (!pendingStatus) return { title: '', description: '' };

    switch (pendingStatus) {
      case 'active':
        return {
          title: 'Activate Workflow',
          description: `Are you sure you want to activate "${workflow.name}"? This workflow will become available for use in approval processes.`,
        };
      case 'inactive':
        return {
          title: 'Deactivate Workflow',
          description: `Are you sure you want to deactivate "${workflow.name}"? Any in-progress approvals using this workflow will continue, but new approvals will not use this workflow.`,
        };
      case 'draft':
        return {
          title: 'Move to Draft',
          description: `Are you sure you want to move "${workflow.name}" back to draft status? This workflow will not be available for use until activated again.`,
        };
      default:
        return { title: '', description: '' };
    }
  };

  const getStatusBadge = (status: WorkflowStage['status']) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Active
          </Badge>
        );
      case 'configured':
        return (
          <Badge variant="secondary" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Configured
          </Badge>
        );
      case 'draft':
        return (
          <Badge variant="outline" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            Draft
          </Badge>
        );
    }
  };

  const getWorkflowStatusBadge = (status: Workflow['status']) => {
    let badgeContent;
    let badgeClass = "";
    let icon;

    switch (status) {
      case 'draft':
        badgeContent = 'Draft';
        badgeClass = "border-gray-300 text-gray-700 bg-gray-50";
        icon = <AlertCircle className="h-3 w-3 mr-1" />;
        break;
      case 'active':
        badgeContent = 'Active';
        badgeClass = "bg-green-500 text-white border-green-500";
        icon = <CheckCircle2 className="h-3 w-3 mr-1" />;
        break;
      case 'inactive':
        badgeContent = 'Inactive';
        badgeClass = "bg-gray-500 text-white border-gray-500";
        icon = <PowerOff className="h-3 w-3 mr-1" />;
        break;
      default:
        return null;
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1">
            <Badge className={`gap-1 ${badgeClass}`}>
              {icon}
              {badgeContent}
              <ChevronDown className="h-3 w-3 ml-1" />
            </Badge>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {status !== 'draft' && (
            <DropdownMenuItem onClick={() => handleStatusChangeRequest('draft')}>
              <AlertCircle className="h-4 w-4 mr-2" />
              Move to Draft
            </DropdownMenuItem>
          )}
          {status !== 'active' && (
            <DropdownMenuItem onClick={() => handleStatusChangeRequest('active')}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Activate
            </DropdownMenuItem>
          )}
          {status !== 'inactive' && (
            <DropdownMenuItem onClick={() => handleStatusChangeRequest('inactive')}>
              <PowerOff className="h-4 w-4 mr-2" />
              Deactivate
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // Create workflow context for the wizard
  const createWorkflowContext = (stage: WorkflowStage): WorkflowContext => {
    return {
      workflowId: workflow.id,
      workflowName: workflow.name,
      currentStageNumber: stage.order,
      currentStageId: stage.id,
      currentStageName: stage.name,
      totalStages: workflow.stages.length,
      allStages: workflow.stages.map((s) => ({
        id: s.id,
        name: s.name,
        order: s.order,
      })),
    };
  };

  // Convert WorkflowStage to ApprovalStage format for the new components
  const convertToApprovalStages = (): ApprovalStage[] => {
    return workflow.stages.map((stage) => {
      // Extract rules from stageData.smartRules
      const smartRules = stage.stageData?.smartRules;
      const hasRules = smartRules && (
        (smartRules.rules && smartRules.rules.length > 0) ||
        (smartRules.onApproved && smartRules.onApproved.type !== 'continue') ||
        (smartRules.onRejected && smartRules.onRejected.type !== 'reject') ||
        smartRules.timeout?.enabled
      );
      
      // Extract notifications from stageData.notifications
      const notifications = stage.stageData?.notifications;
      const hasNotifications = notifications && (
        (notifications.onApproved && notifications.onApproved.length > 0) ||
        (notifications.onRejected && notifications.onRejected.length > 0)
      );
      
      return {
        id: stage.id,
        stageNumber: stage.order,
        name: stage.name,
        description: stage.description,
        approvers: stage.stageData?.approver ? [{
          id: stage.stageData.approver.specificUserId || '',
          name: stage.stageData.approver.specificUserName || '',
          email: '',
          type: stage.stageData.approver.approverType === 'role' ? 'role' : 'user',
          role: stage.stageData.approver.roleType,
        }] : [],
        // Rules are tracked via routingConfigured flag - ApprovalStage.rules uses a different format
        // than the wizard's smart rules, so we leave it empty and use routingConfigured for display
        rules: [],
        notificationSettings: {
          enabled: hasNotifications || false,
        },
        // Include routing info for display purposes
        routingConfigured: hasRules || false,
      };
    });
  };

  const handleEditStageFromList = (stage: ApprovalStage) => {
    const workflowStage = workflow.stages.find((s) => s.id === stage.id);
    if (workflowStage) {
      handleEditStage(workflowStage.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Workflows
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{workflow.name}</h1>
            {getWorkflowStatusBadge(workflow.status)}
          </div>
          {workflow.description && (
            <p className="text-muted-foreground">{workflow.description}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Last modified: {new Date(workflow.updatedAt).toLocaleDateString()}</span>
            {workflow.assignedToCount !== undefined && (
              <span>Assigned to: {workflow.assignedToCount} employees</span>
            )}
          </div>
        </div>
      </div>

      <Separator />

      {/* Placeholder Stage Warning */}
      {hasPlaceholders && (
        <PlaceholderStageWarning 
          placeholders={placeholders}
          onCreateStage={onAddStage ? (stageName) => onAddStage(stageName) : undefined}
        />
      )}

      {/* View Toggle and Add Stage Button */}
      {workflow.stages.length > 0 ? (
        <div className="flex items-center justify-between">
          <WorkflowViewToggle currentView={currentView} onViewChange={setCurrentView} />
          {onAddStage && (
            <Button onClick={() => onAddStage()} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Stage
            </Button>
          )}
        </div>
      ) : null}

      {/* Workflow Views */}
      {workflow.stages.length > 0 ? (
        <>
          {currentView === 'list' ? (
            <WorkflowListView 
              stages={convertToApprovalStages()} 
              onEditStage={handleEditStageFromList}
              showPlaceholderInfo={hasPlaceholders}
            />
          ) : (
            <WorkflowVisualFlow 
              stages={convertToApprovalStages()} 
              onEditStage={(stageId) => handleEditStage(stageId)}
            />
          )}
        </>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <FileText className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">No stages yet</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Get started by adding your first approval stage to this workflow.
                </p>
              </div>
              {onAddStage && (
                <Button onClick={() => onAddStage()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Stage
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stage Wizard */}
      {wizardOpen && editingStage && (
        <ApprovalStageWizard
          isOpen={wizardOpen}
          onClose={handleCloseWizard}
          onSave={handleSaveStage}
          onSaveDraft={onSaveDraft ? (stageData) => onSaveDraft(editingStage.id, stageData) : undefined}
          onSaveAndAddAnother={handleSaveAndAddAnother}
          existingStage={editingStage.stageData}
          workflowContext={createWorkflowContext(editingStage)}
          initialStageName={initialStageName}
        />
      )}

      {/* Status Change Confirmation Dialog */}
      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getStatusDialogContent().title}</AlertDialogTitle>
            <AlertDialogDescription>
              {getStatusDialogContent().description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingStatus(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleStatusChangeConfirm}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
